'use client';

import { useState } from 'react';

interface ArticleShareButtonsProps {
  title: string;
  slug: string;
}

export default function ArticleShareButtons({
  title,
  slug,
}: ArticleShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  // Dapatkan URL lengkap artikel
  const getShareUrl = () => {
    if (typeof window !== 'undefined') {
      return window.location.href;
    }
    return `https://alkindi.id/blog/${slug}`;
  };

  // Fungsi untuk membagikan ke Twitter
  const shareToTwitter = () => {
    const url = getShareUrl();
    const text = `${title} ${url} via @alkindivv`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
      '_blank'
    );
  };

  // Fungsi untuk membagikan ke LinkedIn
  const shareToLinkedIn = () => {
    const url = getShareUrl();
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  // Fungsi untuk membagikan ke WhatsApp
  // const shareToWhatsApp = () => {
  //   const url = getShareUrl();
  //   window.open(
  //     `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
  //     '_blank'
  //   );
  // };

  // Fungsi untuk menyalin link
  const copyLink = () => {
    const url = getShareUrl();
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex items-center gap-3">
      {/* Twitter Share */}
      <div className="relative group">
        <button
          onClick={shareToTwitter}
          className="flex items-center justify-center transition-all duration-300 hover:scale-110 p-1"
          aria-label="Share to Twitter"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-neutral-500 group-hover:text-blue-400 size-4"
          >
            <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
          </svg>
        </button>
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
          <div className="bg-gray-800 text-white text-xs md:text-sm rounded py-1 px-2 whitespace-nowrap">
            Share to Twitter
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </div>
      </div>

      {/* LinkedIn Share */}
      <div className="relative group">
        <button
          onClick={shareToLinkedIn}
          className="flex items-center justify-center transition-all duration-300 hover:scale-110 p-1"
          aria-label="Share to LinkedIn"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-neutral-500 group-hover:text-blue-600 size-4"
          >
            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
            <rect x="2" y="9" width="4" height="12" />
            <circle cx="4" cy="4" r="2" />
          </svg>
        </button>
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
          <div className="bg-gray-800 text-white text-xs md:text-sm rounded py-1 px-2 whitespace-nowrap">
            Share to LinkedIn
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </div>
      </div>

      {/* Copy Link */}
      <div className="relative group">
        <button
          onClick={copyLink}
          className={`flex items-center justify-center transition-all duration-300 hover:scale-110 p-1 ${copied ? 'text-green-500' : ''}`}
          aria-label="Copy link"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`${copied ? 'text-green-400' : 'text-neutral-500'} size-4`}
          >
            {copied ? (
              <>
                <polyline points="20 6 9 17 4 12" />
              </>
            ) : (
              <>
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </>
            )}
          </svg>
        </button>
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
          <div className="bg-gray-800 text-white text-xs md:text-sm rounded py-1 px-2 whitespace-nowrap">
            {copied ? 'Copied!' : 'Copy'}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
